/**
 * SharePanel — admin UI to mint, list and revoke magic-link shares for an
 * assessment run. Each share is a read-only, recipient-named public URL
 * that exposes a redacted maturity summary (radar + level + top moves).
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link2, Copy, Trash2, Sparkles, RefreshCw, Eye, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const PUBLIC_BASE = process.env.REACT_APP_BACKEND_URL || "";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

const fmtDateTime = (iso) => {
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function SharePanel({ runId }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ recipient_name: "", recipient_email: "", sponsor_message: "", expires_in_days: 30 });

  const reload = useCallback(async () => {
    if (!runId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/assessments/runs/${runId}/shares`);
      setShares(data.items || []);
    } catch (e) {
      toast.error("Failed to load shares");
    } finally { setLoading(false); }
  }, [runId]);

  useEffect(() => { reload(); }, [reload]);

  const createShare = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/assessments/runs/${runId}/share`, form);
      const url = `${PUBLIC_BASE}/shared/${data.token}`;
      try { await navigator.clipboard.writeText(url); toast.success("Share link copied to clipboard"); }
      catch { toast.success(`Share link ready: ${url.slice(0, 50)}…`); }
      setShowForm(false);
      setForm({ recipient_name: "", recipient_email: "", sponsor_message: "", expires_in_days: 30 });
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create share");
    } finally { setSubmitting(false); }
  };

  const shareAndEmail = async (e) => {
    e.preventDefault();
    if (!form.recipient_name.trim() || !form.recipient_email.trim()) {
      toast.error("Recipient name and email are required to send by email");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/assessments/runs/${runId}/share-and-email`, form);
      if (data.email_sent) {
        toast.success(`Share emailed to ${form.recipient_email} · PNG executive summary attached`);
      } else {
        toast.message(`Share created but email skipped — link: ${data.share_url}`);
      }
      setShowForm(false);
      setForm({ recipient_name: "", recipient_email: "", sponsor_message: "", expires_in_days: 30 });
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send share email");
    } finally { setSubmitting(false); }
  };

  const revoke = async (token) => {
    if (!window.confirm("Revoke this share link? The recipient will see a 'revoked' message immediately.")) return;
    try {
      await api.delete(`/admin/assessments/shares/${token}`);
      toast.success("Share revoked");
      await reload();
    } catch { toast.error("Revoke failed"); }
  };

  const copy = async (token) => {
    const url = `${PUBLIC_BASE}/shared/${token}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error(`Copy failed — link is ${url}`); }
  };

  return (
    <div className="border border-zinc-200 bg-white" data-testid="share-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 bg-zinc-50">
        <div className="flex items-center gap-2">
          <Link2 size={14} className="text-[#7737F6]" />
          <div className="text-[12px] font-semibold tracking-[0.18em] uppercase text-zinc-700">Magic-link shares</div>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={reload} className="text-zinc-500 hover:text-zinc-800 text-[11.5px] inline-flex items-center gap-1" aria-label="Refresh"><RefreshCw size={11} /></button>
          <button type="button" onClick={() => setShowForm((v) => !v)} className="text-[#7737F6] hover:text-[#5D28C7] text-[12px] inline-flex items-center gap-1" data-testid="share-create-toggle">
            <Sparkles size={12} /> {showForm ? "Cancel" : "Create share"}
          </button>
        </div>
      </div>
      {showForm ? (
        <form onSubmit={createShare} className="px-4 py-4 border-b border-zinc-200 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="share-form">
          <input type="text" placeholder="Recipient name" value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} className="border border-zinc-300 px-3 py-2 text-[13px] focus:outline-none focus:border-[#7737F6]" data-testid="share-recipient-name" />
          <input type="email" placeholder="Recipient email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} className="border border-zinc-300 px-3 py-2 text-[13px] focus:outline-none focus:border-[#7737F6]" data-testid="share-recipient-email" />
          <textarea placeholder="Sponsor message — paste the SteerCo framing they should read first" value={form.sponsor_message} onChange={(e) => setForm({ ...form, sponsor_message: e.target.value })} className="md:col-span-2 border border-zinc-300 px-3 py-2 text-[13px] focus:outline-none focus:border-[#7737F6] min-h-[80px]" data-testid="share-sponsor-message" />
          <label className="text-[12px] text-zinc-700 flex items-center gap-2">
            Expires in
            <input type="number" min="1" max="365" value={form.expires_in_days} onChange={(e) => setForm({ ...form, expires_in_days: Number(e.target.value) })} className="border border-zinc-300 px-2 py-1 text-[13px] w-20" data-testid="share-expires" />
            days
          </label>
          <button type="submit" disabled={submitting} className="ek-btn ek-btn-primary inline-flex items-center justify-center gap-2 text-[12.5px] px-4 py-2 md:col-start-2 disabled:opacity-50" data-testid="share-create-submit">
            {submitting ? "Creating…" : (<><Link2 size={12} /> Create + copy link</>)}
          </button>
          <button
            type="button"
            onClick={shareAndEmail}
            disabled={submitting}
            className="ek-btn inline-flex items-center justify-center gap-2 text-[12.5px] px-4 py-2 bg-[#0B0B0C] text-white hover:bg-zinc-800 md:col-start-2 disabled:opacity-50"
            data-testid="share-and-email-submit"
            title="Mints a share token, renders the PNG executive summary, and emails both to the recipient in one go"
          >
            {submitting ? "Sending…" : (<><Send size={12} /> Send share + PNG to sponsor</>)}
          </button>
        </form>
      ) : null}
      <div className="overflow-x-auto">
        <table className="min-w-full text-[12.5px]">
          <thead className="bg-white">
            <tr className="text-left border-b border-zinc-200 text-[10.5px] uppercase tracking-wider text-zinc-500">
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2">Recipient</th>
              <th className="px-3 py-2">Expires</th>
              <th className="px-3 py-2">Views</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-zinc-400 italic">Loading…</td></tr>
            ) : shares.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-zinc-500">
                <div className="font-medium text-zinc-700">No shares yet.</div>
                <div className="mt-1 text-[12px] text-zinc-500">Use Create share to mint a read-only link a sponsor can open without logging in.</div>
              </td></tr>
            ) : shares.map((s) => {
              const expired = s.expires_at && s.expires_at < new Date().toISOString();
              return (
                <tr key={s.token} className="border-t border-zinc-100" data-testid={`share-row-${s.token}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDateTime(s.created_at)}</td>
                  <td className="px-3 py-2">
                    <div className="text-zinc-900">{s.recipient_name || <span className="text-zinc-500 italic">—</span>}</div>
                    {s.recipient_email ? <div className="text-zinc-500 text-[11.5px]">{s.recipient_email}</div> : null}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDate(s.expires_at)}</td>
                  <td className="px-3 py-2">{s.view_count || 0}{s.last_viewed_at ? <span className="text-[10.5px] text-zinc-500 ml-1">· {fmtDateTime(s.last_viewed_at)}</span> : null}</td>
                  <td className="px-3 py-2">
                    {s.revoked ? (<span className="inline-flex items-center px-2 py-0.5 text-[10.5px] bg-red-50 text-red-800 border border-red-200">revoked</span>)
                      : expired ? (<span className="inline-flex items-center px-2 py-0.5 text-[10.5px] bg-zinc-100 text-zinc-700 border border-zinc-300">expired</span>)
                      : (<span className="inline-flex items-center px-2 py-0.5 text-[10.5px] bg-emerald-50 text-emerald-800 border border-emerald-200">active</span>)}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {!s.revoked && !expired ? (
                      <>
                        <button type="button" onClick={() => copy(s.token)} className="text-[#7737F6] hover:text-[#5D28C7] text-[11.5px] inline-flex items-center gap-1 mr-3" data-testid={`share-copy-${s.token}`}>
                          <Copy size={11} /> Copy link
                        </button>
                        <a href={`${PUBLIC_BASE}/shared/${s.token}`} target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-zinc-800 text-[11.5px] inline-flex items-center gap-1 mr-3" data-testid={`share-open-${s.token}`}>
                          <Eye size={11} /> Open
                        </a>
                      </>
                    ) : null}
                    {!s.revoked ? (
                      <button type="button" onClick={() => revoke(s.token)} className="text-red-700 hover:text-red-900 text-[11.5px] inline-flex items-center gap-1" data-testid={`share-revoke-${s.token}`}>
                        <Trash2 size={11} /> Revoke
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
